import type { KeyboardEvent } from 'react';

export interface ClickableRowProps {
  role?: 'button';
  tabIndex?: 0;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * Returns props that turn a non-button element (typically a `<tr>`) into a
 * keyboard-accessible activator. The element gets `role="button"`, `tabIndex=0`
 * and an `onKeyDown` handler that fires `onActivate` for Enter and Space.
 *
 * Inner interactive controls inside the row should still call
 * `e.stopPropagation()` on their own click handlers so they aren't intercepted
 * (existing pattern in the list pages).
 *
 * Pass a falsy `onActivate` (e.g. when the row is read-only) to get an empty
 * props object — the row stays a plain `<tr>`.
 *
 * This is a plain helper, not a hook, so it is safe to call inside
 * `.map(...)` callbacks.
 */
export function clickableRowProps(onActivate: (() => void) | null | undefined): ClickableRowProps {
  if (typeof onActivate !== 'function') return {};
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onActivate(),
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
  };
}
